'use client';

import { Button } from '@/components/ui/Button';
import { CloseIcon } from '@/components/ui/icons/CloseIcon';
import { cn } from '@/lib/ui/cn';
import type { ComponentClinicProps } from '@/types/components';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

const ctaClass = [
  'h-12 rounded-[50px] px-5 py-3 font-bold opacity-100 shadow-none',
  'text-[18px] leading-[130%] md:text-[18px]',
  'w-full md:w-full',
  'lg:flex-1 lg:shrink',
].join(' ');

interface LocationMapCardProps {
  clinic: ComponentClinicProps;
  onClose: () => void;
  className?: string;
}

export function LocationMapCard({
  clinic,
  onClose,
  className,
}: LocationMapCardProps) {
  const t = useTranslations('LocationsMap');
  const { name, slug, address, telephone, latitude, longitude } = clinic;

  const directions =
    latitude != null && longitude != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`
      : undefined;

  return (
    <div
      data-component='location-map-card'
      data-testid='location-map-card'
      className={cn(
        'border-sh-gray flex w-[318px] max-w-[calc(100vw-2rem)] flex-col gap-4 rounded-3xl border bg-white p-4',
        'text-[18px] leading-[130%] text-black lg:text-[21px]',
        className
      )}
    >
      <div className='flex items-center justify-between gap-2'>
        {name && <h3 className='font-bold'>{name}</h3>}
        <button
          type='button'
          onClick={onClose}
          aria-label={t('close')}
          data-testid='location-map-card-close'
          className='bg-sh-paper flex size-8 flex-none cursor-pointer items-center justify-center rounded-full'
        >
          <CloseIcon className='size-3.5' />
        </button>
      </div>

      {address && <p className='whitespace-pre-line'>{address}</p>}
      {telephone && (
        <p>
          <a href={`tel:${telephone}`}>{telephone}</a>
        </p>
      )}

      <div className='flex w-full flex-col gap-4 lg:flex-row'>
        {slug && (
          <Button
            variant='dark'
            asChild
            className={cn(ctaClass, 'bg-sh-dark-gray')}
          >
            <Link href={`/locations/${slug}`}>{t('viewCenter')}</Link>
          </Button>
        )}
        {directions && (
          <Button
            variant='light'
            asChild
            className={cn(ctaClass, 'border-sh-gray border bg-white')}
          >
            <a href={directions} target='_blank' rel='noopener noreferrer'>
              {t('directions')}
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}
